// ============================================================================
// STUDIO CANETTE — OFFRES, PACKS ET DROITS
//
// Source unique, importée telle quelle par le client (page Tarifs, page Compte,
// garde-fous d'interface) ET par le serveur (checkout Stripe, attribution des
// crédits au webhook, vérification des droits). Un prix ou un quota qui
// existerait à deux endroits finirait par diverger : il n'existe qu'ici.
//
// L'unité de facturation est le CRÉDIT. 1 crédit = 1 image générée. Pas de
// second compteur, pas de « jetons » ni de « tokens » : on vend des photos.
//
// ÉCONOMIE UNITAIRE (relevés API de juillet 2026, à re-vérifier avant toute
// retouche de prix) : Gemini 3 Pro Image (Nano Banana Pro) coûte 0,134 $
// l'image 1K/2K et 0,24 $ la 4K ; gpt-image-2 en qualité haute monte à
// ~0,21 $. Soit, références comprises : ≈ 0,13 € l'image 2K, ≈ 0,22 € la 4K —
// et comme la TVA sur ces achats hors UE est autoliquidée sans être
// récupérable en franchise de TVA, compter ≈ 0,16 € (2K) et ≈ 0,26 € (4K)
// sortis de poche.
//
// L'ÉDITEUR EST EN MICRO-ENTREPRISE : les cotisations (~25,6 % en BNC 2026)
// se calculent sur le CHIFFRE D'AFFAIRES, pas sur le bénéfice. Aucun coût
// d'API n'est déductible de quoi que ce soit. Règle de calibrage : le prix
// unitaire d'une image doit rester ≥ 3× son coût TTC — soit un plancher de
// 0,50 € la 2K et 0,78 € la 4K. Une offre qui passe sous ce plancher doit
// faire recalculer cette page avant d'être publiée.
// ============================================================================

/** Coût en crédits d'une génération. Une seule opération est facturée. */
export const CREDIT_COST_GENERATION = 1;

/* ------------------------------------------------------------------ *
 * VIDÉO — LE PRIX D'UNE SECONDE                                       *
 *                                                                     *
 * Une vidéo ne coûte pas « un peu plus » qu'une image : elle coûte     *
 * un ordre de grandeur de plus, et son échec coûte autant que son      *
 * succès. Le barème est donc construit, pas deviné.                    *
 *                                                                     *
 * CE QU'ELLE NOUS COÛTE. Les modèles image → vidéo se facturent à la   *
 * seconde : compter 0,10 à 0,25 € HT la seconde selon le modèle et la  *
 * définition (Seedance et Kling en bas de fourchette, la famille Veo   *
 * en haut). On calibre sur le HAUT — 0,25 € HT, soit 0,30 € TTC — pour *
 * que le barème tienne quel que soit le modèle choisi. Une séquence de *
 * six secondes revient donc à ≈ 1,80 € TTC.                            *
 *                                                                     *
 * TROIS CHOSES QUE LE BARÈME DOIT ABSORBER, en plus du coût brut :     *
 *   • les ÉCHECS. Un modèle vidéo refuse, dépasse le délai ou rend un  *
 *     plan inutilisable bien plus souvent qu'un modèle d'image. On     *
 *     rembourse l'utilisateur — donc c'est nous qui payons le raté.    *
 *   • le STOCKAGE et la BANDE PASSANTE : 10 à 20 Mo par fichier contre *
 *     2 Mo pour une image, conservés aussi longtemps.                  *
 *   • la MICRO-ENTREPRISE : les cotisations (~25,6 %) portent sur le   *
 *     CHIFFRE D'AFFAIRES, aucun coût d'API n'est déductible. C'est la  *
 *     même contrainte que pour l'image, rappelée plus haut.            *
 *                                                                     *
 * LE BARÈME. Deux crédits la seconde, huit crédits au minimum. À la    *
 * valeur d'un crédit en pack (0,60 à 0,78 €), six secondes coûtent     *
 * douze crédits, soit 7 à 9 € — pour un coût de ≈ 1,80 €. Le rapport   *
 * tourne autour de 4 à 5×, au-dessus du plancher de 3× retenu pour     *
 * l'image, et c'est volontaire : c'est cette marge qui paie les rendus *
 * ratés qu'on ne facture pas.                                          *
 *                                                                     *
 * CÔTÉ CLIENT, ça se dit en une phrase : « une vidéo vaut une dizaine  *
 * de photos ». C'est vrai, et c'est très en dessous du prix d'un       *
 * motion designer pour la même chose.                                  *
 * ------------------------------------------------------------------ */

/** Coût en crédits d'une seconde de vidéo. */
export const CREDIT_COST_VIDEO_PER_SECOND = 2;

/** Plancher : en deçà, on ne couvrirait pas un échec remboursé. */
export const CREDIT_COST_VIDEO_MIN = 8;

/** Durées proposées à l'interface, en secondes. */
export const VIDEO_DURATIONS = [4, 6, 8, 10, 12];

/**
 * Coût d'une vidéo, en crédits. Source unique : l'interface l'affiche AVANT le
 * clic, le serveur le débite après. Les deux appellent cette fonction — un
 * barème recopié d'un côté finirait par mentir à l'utilisateur.
 *
 * @param {number} seconds
 * @returns {number}
 */
export function videoCost(seconds) {
  const s = Number(seconds);
  if (!Number.isFinite(s) || s <= 0) return CREDIT_COST_VIDEO_MIN;
  return Math.max(CREDIT_COST_VIDEO_MIN, Math.ceil(s * CREDIT_COST_VIDEO_PER_SECOND));
}

/** Crédits offerts à la création d'un espace de travail (une seule fois). */
export const SIGNUP_BONUS_CREDITS = 5;

/**
 * Plafond de report : on remet le solde à `monthlyCredits` chaque mois, mais on
 * laisse cumuler jusqu'à 2× l'allocation. Sans plafond, un compte dormant six
 * mois repart avec 3 000 crédits ; sans report du tout, l'abonné qui saute un
 * mois a l'impression d'avoir payé pour rien.
 */
export const ROLLOVER_FACTOR = 2;

/** Remise annuelle : douze mois facturés dix. */
export const YEARLY_MONTHS_BILLED = 10;

/* ------------------------------------------------------------------ *
 * Les offres                                                          *
 * ------------------------------------------------------------------ */

/**
 * @typedef {Object} Plan
 * @property {string}  id              identifiant technique (= colonne `plan`)
 * @property {string}  name            nom commercial
 * @property {string}  tagline         à qui ça s'adresse, en une ligne
 * @property {number}  monthlyPrice    € HT par mois, facturation mensuelle
 * @property {number}  yearlyPrice     € HT par mois, facturation annuelle
 * @property {number}  monthlyCredits  images incluses par mois
 * @property {number}  seats           sièges inclus
 * @property {string[]} highlights     puces affichées sur la carte
 * @property {string}  accent          jeton de couleur (voir tokens.css)
 * @property {boolean} [featured]      carte mise en avant
 */

/** @type {Record<string, Plan>} */
export const PLANS = {
  free: {
    id: 'free',
    name: 'Essai',
    tagline: 'Pour voir si la canette sort juste.',
    monthlyPrice: 0,
    yearlyPrice: 0,
    monthlyCredits: 0,          // dotation unique à l'inscription, pas de recharge
    seats: 1,
    accent: 'silver',
    highlights: [
      `${SIGNUP_BONUS_CREDITS} images offertes à l'inscription`,
      'Les 8 formats de canette réels',
      'Artwork 360° sur la zone d\'impression exacte',
      'L\'angle posé en 3D est l\'angle rendu',
      'Export 2K — filigrane Studio Canette'
    ]
  },
  solo: {
    id: 'solo',
    name: 'Solo',
    tagline: 'Le brasseur, la marque, le graphiste indépendant.',
    monthlyPrice: 39,
    yearlyPrice: 32,
    monthlyCredits: 50,
    seats: 1,
    accent: 'cyan',
    highlights: [
      '50 images par mois',
      'Génération 2K, export sans filigrane',
      'PNG détouré inclus',
      'Projets illimités, sauvegardés dans le cloud',
      'Lien de BAT : le client tourne la canette en 360°',
      'Bibliothèque de rendus conservée'
    ]
  },
  studio: {
    id: 'studio',
    name: 'Studio',
    tagline: 'L\'équipe marketing qui alimente ses réseaux chaque semaine.',
    monthlyPrice: 99,
    yearlyPrice: 82,
    monthlyCredits: 100,
    seats: 3,
    accent: 'yellow',
    featured: true,
    highlights: [
      '100 images par mois',
      'Génération 4K — la définition d\'un packshot imprimable',
      '3 sièges — designers et chefs de produit sur le même espace',
      'File de génération prioritaire',
      'Déclinaisons de gamme en un clic'
    ]
  },
  agence: {
    id: 'agence',
    name: 'Agence',
    tagline: 'Vous produisez pour plusieurs marques de boisson.',
    monthlyPrice: 249,
    yearlyPrice: 207,
    monthlyCredits: 250,
    seats: 10,
    accent: 'pink',
    highlights: [
      '250 images par mois, générées en 4K',
      '10 sièges, espaces séparés par marque',
      'Exports en marque blanche',
      'Support dédié, réponse sous 24 h ouvrées',
      'Formats sur mesure (cotes hors catalogue)'
    ]
  }
};

/** Ordre d'affichage sur la page Tarifs. */
export const PLAN_ORDER = ['free', 'solo', 'studio', 'agence'];

/** Un plan est-il payant ? (les droits « premium » en découlent) */
export const isPaidPlan = plan => plan === 'solo' || plan === 'studio' || plan === 'agence';

/* ------------------------------------------------------------------ *
 * Droits par offre                                                    *
 * ------------------------------------------------------------------ */

/**
 * @typedef {Object} Features
 * @property {boolean} watermark        appose le filigrane sur les exports
 * @property {number}  maxExportPx      côté long maximum d'un export PNG
 * @property {'2K'|'4K'} renderSize     définition demandée au modèle d'image —
 *                                      la 4K coûte près du double de la 2K chez
 *                                      le fournisseur, elle est donc réservée
 *                                      aux offres dont le prix la couvre
 * @property {boolean} dieline          téléchargement du gabarit 300 dpi.
 *                                      Ouvert à TOUTES les offres : notre
 *                                      public arrive avec son BAT, le gabarit
 *                                      est une commodité, pas un argument de
 *                                      vente — le verrouiller n'a jamais fait
 *                                      signer personne
 * @property {boolean} cloudProjects    synchronisation des projets
 * @property {boolean} shareLinks       lien de BAT : le client tourne la
 *                                      canette en 360° dans son navigateur,
 *                                      sans compte. C'est un outil de relation
 *                                      client, donc de facturation : il
 *                                      s'ouvre au premier abonnement
 * @property {boolean} transparentPng   export PNG détouré
 * @property {boolean} priorityQueue    file de génération prioritaire
 * @property {boolean} whiteLabel       exports sans mention Studio Canette
 * @property {boolean} customFormats    cotes hors catalogue
 * @property {number}  seats
 */

/** @type {Record<string, Features>} */
export const PLAN_FEATURES = {
  free: {
    watermark: true, maxExportPx: 2048, renderSize: '2K', dieline: true,
    cloudProjects: false, shareLinks: false, transparentPng: false, priorityQueue: false,
    whiteLabel: false, customFormats: false, seats: 1
  },
  solo: {
    watermark: false, maxExportPx: 2400, renderSize: '2K', dieline: true,
    cloudProjects: true, shareLinks: true, transparentPng: true, priorityQueue: false,
    whiteLabel: false, customFormats: false, seats: 1
  },
  studio: {
    watermark: false, maxExportPx: 4096, renderSize: '4K', dieline: true,
    cloudProjects: true, shareLinks: true, transparentPng: true, priorityQueue: true,
    whiteLabel: false, customFormats: false, seats: 3
  },
  agence: {
    watermark: false, maxExportPx: 4096, renderSize: '4K', dieline: true,
    cloudProjects: true, shareLinks: true, transparentPng: true, priorityQueue: true,
    whiteLabel: true, customFormats: true, seats: 10
  }
};

export const featuresFor = plan => PLAN_FEATURES[plan] ?? PLAN_FEATURES.free;

/* ------------------------------------------------------------------ *
 * Packs de crédits — hors abonnement, sans expiration                 *
 * ------------------------------------------------------------------ */

/**
 * @typedef {Object} Pack
 * @property {string} id
 * @property {string} name
 * @property {number} credits
 * @property {number} price     € HT
 * @property {string} accent
 * @property {string} [note]
 */

/** @type {Pack[]} */
export const PACKS = [
  { id: 'pack50', name: '50 images', credits: 50, price: 39, accent: 'lime' },
  { id: 'pack100', name: '100 images', credits: 100, price: 69, accent: 'cyan', note: 'Le plus pris pour un lancement de gamme' },
  { id: 'pack200', name: '200 images', credits: 200, price: 119, accent: 'violet' }
];

export const getPack = id => PACKS.find(p => p.id === id) ?? null;

/** Prix unitaire d'une image, pour comparer honnêtement les offres. */
export const unitPrice = ({ price, credits }) => price / credits;

/* ------------------------------------------------------------------ *
 * Correspondance Stripe                                               *
 *                                                                     *
 * Les identifiants de prix vivent dans l'environnement — un compte     *
 * Stripe de test et un compte de production n'ont pas les mêmes.       *
 * Côté serveur : STRIPE_PRICE_SOLO_MONTH, …_YEAR, STRIPE_PRICE_PACK50…  *
 * ------------------------------------------------------------------ */

export const priceEnvKey = (planId, interval) =>
  `STRIPE_PRICE_${planId.toUpperCase()}_${interval === 'year' ? 'YEAR' : 'MONTH'}`;

export const packEnvKey = packId => `STRIPE_PRICE_${packId.toUpperCase()}`;

/* ------------------------------------------------------------------ *
 * Formats de canette — repris du moteur 3D, pour l'affichage marketing *
 * (la vérité géométrique reste dans src/studio/canstudio.js)          *
 * ------------------------------------------------------------------ */

export const FORMAT_SHOWCASE = [
  { id: 'slim250', label: 'Slim 250 ml', dims: '⌀ 53,0 × 133,4 mm', use: 'Energy, tonic premium' },
  { id: 'sleek250', label: 'Sleek 250 ml', dims: '⌀ 58,1 × 133,3 mm', use: 'Spritz, bière sans alcool' },
  { id: 'squat250', label: 'Stubby 250 ml', dims: '⌀ 66,3 × 91,5 mm', use: 'Apéritif, mixer' },
  { id: 'std330', label: 'Standard 33 cl', dims: '⌀ 66,3 × 115,2 mm', use: 'Le format de rayon' },
  { id: 'sleek330', label: 'Sleek 33 cl', dims: '⌀ 58,1 × 145,4 mm', use: 'Craft élancée' },
  { id: 'slim330', label: 'Slim 33 cl', dims: '⌀ 53,0 × 168,5 mm', use: 'Hard seltzer' },
  { id: 'std440', label: 'Standard 44 cl', dims: '⌀ 66,3 × 149,9 mm', use: 'Bière pinte' },
  { id: 'std500', label: 'Standard 50 cl', dims: '⌀ 66,3 × 168,0 mm', use: 'Bière, cidre' }
];

/* ------------------------------------------------------------------ *
 * Formatage                                                           *
 * ------------------------------------------------------------------ */

export const euros = n =>
  n === 0 ? 'Gratuit'
    : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: n % 1 ? 2 : 0 }).format(n);

export const credits = n => new Intl.NumberFormat('fr-FR').format(n);
